import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, filter, map, merge, take } from 'rxjs';

import { PageBasedComponent } from '@app/pages/page.based.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { TeamMessage } from '@app/shared/classes/team-message';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { AppDataState } from '@app/shared/stores/app-data.state';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { DataGridColumnDef, GetTeamMessageSearchByList } from './team-message.constant';
import { TeamMessageEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/team-message-edit-modal-dialog/team-message-edit-modal-dialog.component';
import { TeamMessageDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/team-message-data-grid-cell.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-team-message',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule, DisableEditButtonDirective,
    SmartSearchFormComponent, SpinnerButtonComponent, TeamMessageDataGridCell],
  templateUrl: './team-message.component.html',
  styleUrl: './team-message.component.css'
})
export class TeamMessageComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.TEAM_MESSAGE_SEARCH_FORM;
  selectedItems: TeamMessage[] = [];

  dataSource$: Observable<TeamMessage[]>;
  teamMessageSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentTeamMessageSearchBy) currentTeamMessageSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_TEAM_MESSAGE)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_TEAM_MESSAGE)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_TEAM_MESSAGE)) response$: Observable<WebApiResponse<TeamMessage[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_TEAM_MESSAGE)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_TEAM_MESSAGE;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentTeamMessageSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.teamMessageSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map(c => GetTeamMessageSearchByList(c)));
  }

  rowSelected = (data: TeamMessage[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetTeamMessage());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetTeamMessage(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_TEAM_MESSAGE;
    this.store.dispatch(new AppDataAction.GetTeamMessage(info));
  }

  onCreate = () => {
    this.dialog.open(TeamMessageEditModalDialogComponent, {
      width: '80%',
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true
    });
  }

  onEdit = () => {
    this.dialog.open(TeamMessageEditModalDialogComponent, {
      width: '80%',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }
}
